import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Header from "../../components/header";
import "../../styles/Settings.css";
import SettingsService from "../../services/SettingsService";

// Componentes de configuração individuais
import AccountSettings from "./tabs/AccountSettings";
import NotificationSettings from "./tabs/NotificationSettings";
import PrivacySettings from "./tabs/PrivacySettings";
import AppearanceSettings from "./tabs/AppearanceSettings";
import SecuritySettings from "./tabs/SecuritySettings";
import ContentSettings from "./tabs/ContentSettings";
import AccessibilitySettings from "./tabs/AccessibilitySettings";
import FeedSettings from "./tabs/FeedSettings";

const SettingsPage = () => {
  const { user } = useSelector((state) => ({ ...state }));
  const [activeTab, setActiveTab] = useState("account");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const tabs = [
    { id: "account", label: "Conta", icon: "👤" },
    { id: "notifications", label: "Notificações", icon: "🔔" },
    { id: "privacy", label: "Privacidade", icon: "🔒" },
    { id: "appearance", label: "Aparência", icon: "🎨" },
    { id: "security", label: "Segurança", icon: "🛡️" },
    { id: "content", label: "Conteúdo", icon: "📱" },
    { id: "accessibility", label: "Acessibilidade", icon: "♿" },
    { id: "feed", label: "Feed", icon: "📰" },
  ];

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await SettingsService.getAllSettings();
      setSettings(data.settings || {});
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
      showToast("Erro ao carregar configurações", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleUpdateSettings = async (category, updates) => {
    try {
      const result = await SettingsService.updateSettings(category, updates);
      
      // Atualizar estado local
      setSettings(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          ...updates
        }
      }));

      showToast(result.message || "Configurações atualizadas com sucesso");
      return result;
    } catch (error) {
      console.error("Erro ao atualizar configurações:", error);
      showToast(error.message || "Erro ao atualizar configurações", "error");
      throw error;
    }
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="settings-loading">
          <div className="settings-spinner"></div>
        </div>
      );
    }

    const props = {
      settings: settings?.[activeTab] || {},
      onUpdate: (updates) => handleUpdateSettings(activeTab, updates),
      showToast,
      user
    };

    switch (activeTab) {
      case "account":
        return <AccountSettings {...props} />;
      case "notifications":
        return <NotificationSettings {...props} />;
      case "privacy":
        return <PrivacySettings {...props} />;
      case "appearance":
        return <AppearanceSettings {...props} />;
      case "security":
        return <SecuritySettings {...props} />;
      case "content":
        return <ContentSettings {...props} />;
      case "accessibility":
        return <AccessibilitySettings {...props} />;
      case "feed":
        return <FeedSettings {...props} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Header page="settings" />
      <div className="settings-page">
        <div className="settings-container">
          {/* Sidebar de Navegação */}
          <aside className="settings-sidebar">
            <h2>Configurações</h2>
            <ul className="settings-nav">
              {tabs.map((tab) => (
                <li
                  key={tab.id}
                  className={`settings-nav-item ${
                    activeTab === tab.id ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </li>
              ))}
            </ul>
          </aside>

          {/* Conteúdo */}
          <main className="settings-content">
            {renderTabContent()}
          </main>
        </div>

        {/* Toast de Feedback */}
        {toast && (
          <div className={`settings-toast ${toast.type}`}>
            <span className="settings-toast-icon">
              {toast.type === "success" ? "✓" : "✕"}
            </span>
            <span className="settings-toast-text">{toast.message}</span>
          </div>
        )}
      </div>
    </>
  );
};

export default SettingsPage;
